import React, { ReactNode, useEffect } from 'react';
import { SelectOption } from 'global';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { FiltersDrover, BaseUIProps, FilterEvent } from 'components/shared/Filters';

import { VOFilters, VOFilterType } from '../types';
import { componentCardToTypeMapper } from './cards';
import { initialVOFiltersValue } from '../constants';

interface Props extends BaseUIProps {
  configuration: VOFilterType[];
  externalFiltersValue?: Partial<VOFilters> | null;
  headerNode?: (filters: Partial<VOFilters>) => ReactNode;
  presetsNode?: (filters: Partial<VOFilters>) => ReactNode;
  externalMeta?: Partial<Record<VOFilterType, SelectOption[]>>;
  onChanged: (filters: Partial<VOFilters>, event: FilterEvent) => void;
}

export const FiltersContainer = ({
  onClose,
  onChanged,
  headerNode,
  presetsNode,
  configuration,
  externalMeta = {},
  externalFiltersValue,
  ...props
}: Props) => {
  const defaultFiltersValues = configuration.reduce<Partial<VOFilters>>((acc, type) => {
    if (initialVOFiltersValue[type]) {
      acc[type] = initialVOFiltersValue[type] as any;
    }

    return acc;
  }, {});

  const form = useForm<Partial<VOFilters>>({
    defaultValues: externalFiltersValue ?? defaultFiltersValues,
  });

  useEffect(() => {
    if (externalFiltersValue) {
      form.reset({
        ...defaultFiltersValues,
        ...externalFiltersValue,
      });
    }
  }, [externalFiltersValue, form]);

  const filtersValue = useWatch({ control: form.control }) as Partial<VOFilters>;

  const handleApplyFilters = async () => {
    onClose();
    onChanged(filtersValue, { type: 'apply' });
  };

  const handleResetFilters = () => {
    form.reset();
    onClose();
    onChanged(defaultFiltersValues, { type: 'reset' });
  };

  return (
    <FormProvider<Partial<VOFilters>> {...form}>
      <FiltersDrover<VOFilterType>
        onClose={onClose}
        externalMeta={externalMeta}
        configuration={configuration}
        componentMap={componentCardToTypeMapper}
        headerNode={headerNode ? headerNode(filtersValue) : undefined}
        presetsNode={presetsNode ? presetsNode(filtersValue) : undefined}
        actions={{ onReset: handleResetFilters, onApply: handleApplyFilters }}
        {...props}
      />
    </FormProvider>
  );
};
